import {
  IconPlus,
  SequenceMenuItemType,
  Button,
  ActiveFilter,
  Link
} from '@sequencehq/core-components'
import Page from 'components/Page'
import { StickyPanelFrame } from 'components/Page/StickyPanelFrame'
import QuotesMagicTable from 'modules/Quotes/QuotesMagicTable'
import { Dispatch, FC, memo, SetStateAction } from 'react'
import { Outlet, useSearchParams } from 'react-router-dom'
import { SavedViewEditor } from 'SavedViews/components/SavedViewEditor'
import { SavedViewTitle } from 'SavedViews/components/SavedViewTitle'
import { useSavedView } from 'SavedViews/hooks/useSavedView'
import { ProtectedButton } from 'lib/rbac'

const Quotes: FC<{
  title?: string
  forcedActiveFilters?: ActiveFilter[]
  kebabMenuItems?: (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ) => SequenceMenuItemType[] | undefined
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  showSegmentedFilters?: boolean
}> = memo(
  ({
    title = 'Quotes',
    forcedActiveFilters = [],
    kebabMenuItems,
    onUpdateSavedFilters,
    showSegmentedFilters = true
  }) => {
    const [searchParams] = useSearchParams()
    const sortBy = searchParams.get('sortBy') ?? null

    const {
      isSavedView,
      savingFilters,
      setSavingFilters,
      forceNewSavedView,
      onSaveFilters,
      onDismissSaveFilters,
      showSavedViewEditor
    } = useSavedView()

    const richTitle = showSavedViewEditor ? (
      <SavedViewEditor
        activeFilters={savingFilters || []}
        sortBy={sortBy}
        onDismiss={onDismissSaveFilters}
        forceNewSavedView={forceNewSavedView}
        entityType="QUOTES"
      />
    ) : (
      <SavedViewTitle
        title={title}
        kebabMenuItems={kebabMenuItems?.(setSavingFilters)}
      />
    )

    return (
      <>
        <StickyPanelFrame data-testid="quotes.listPage">
          <Page
            title={title}
            richTitle={richTitle}
            paddingBottom={0}
            addNewConfig={
              savingFilters
                ? undefined
                : {
                    button: (
                      <ProtectedButton
                        permission={[
                          'org.quote:create',
                          'org.quote:content.update'
                        ]}
                        needsEveryPermission
                      >
                        <Button
                          as={Link.Unstyled}
                          to="/quotes/new"
                          variant="primary"
                          data-testid="quotes.new"
                          leadingIcon={<IconPlus color="inherit" />}
                        >
                          New quote
                        </Button>
                      </ProtectedButton>
                    )
                  }
            }
          >
            <QuotesMagicTable
              onSaveFilters={onSaveFilters}
              onUpdateSavedFilters={
                isSavedView ? onUpdateSavedFilters : undefined
              }
              forcedActiveFilters={forcedActiveFilters}
              lockedControls={savingFilters && savingFilters.length > 0}
              showSegmentedFilters={showSegmentedFilters}
            />
          </Page>
        </StickyPanelFrame>

        <Outlet />
      </>
    )
  }
)

export default Quotes
